/**
 * Pure cron <-> schedule-cadence mapping for the Agent Builder's schedule picker (#152).
 *
 * The builder offers a small, closed set of scheduling cadences (None / Every N minutes /
 * Hourly / Daily / Weekly) rather than a free-form cron string, so an operator never has
 * to hand-write cron. Each cadence maps to a canonical 5-field cron expression:
 *
 *   - minutes →  step-minute cron "N * * * *" (run every N minutes, N ∈ {5,10,15,30});
 *                N=60 → `0 * * * *`
 *   - hourly  →  `M * * * *`     (run every hour at minute M, M = 0–59)
 *   - daily   →  `0 H * * *`     (run every day at hour H:00, H = 0–23)
 *   - weekly  →  `0 H * * D`     (run weekly on day-of-week D at hour H:00, D = 0–6, Sun=0)
 *
 * (The step form is the standard cron "asterisk-slash-N" in the minute field.)
 *
 * `parseCron` is the reverse: given a stored `schedule` trigger's cron it recovers the
 * cadence + fields so reopening a published agent restores the picker. A step-minute cron
 * maps to the minutes cadence; a plain `0 * * * *` stays hourly (minute 0) — only the step
 * form is treated as "every N minutes". Anything that does not match one of the canonical
 * shapes returns `null` (the caller falls back to a raw/unknown cron — the builder never
 * silently rewrites an expression it can't model).
 *
 * Kept in a pure module (no React/MUI imports) so it is unit-tested directly in the
 * `node`-env web vitest runner without booting a component tree.
 */

/** The scheduling cadence chosen in the builder. `none` emits no schedule trigger. */
export type ScheduleCadence = 'none' | 'minutes' | 'hourly' | 'daily' | 'weekly';

/**
 * The closed set of interval choices (in minutes) for the "Every N minutes" cadence.
 * The server enforces a hard minimum of every-5-minutes at publish, so 5 is the floor.
 * 60 is included as an "every hour, on the hour" convenience that maps to `0 * * * *`.
 */
export const MINUTE_INTERVAL_CHOICES = [5, 10, 15, 30, 60] as const;
export type MinuteInterval = (typeof MINUTE_INTERVAL_CHOICES)[number];

/** Default interval for the "Every N minutes" cadence. */
export const DEFAULT_MINUTE_INTERVAL: MinuteInterval = 10;

const isMinuteInterval = (n: number): n is MinuteInterval =>
  (MINUTE_INTERVAL_CHOICES as readonly number[]).includes(n);

/**
 * The fully-resolved schedule selection: the cadence plus the fields relevant to it.
 * `intervalMinutes` is used by minutes; `minute` by hourly; `hour` by daily/weekly;
 * `dayOfWeek` by weekly. Fields not relevant to the active cadence are ignored when
 * building the cron, but kept in state so switching cadences preserves prior picks.
 */
export interface ScheduleSelection {
  cadence: ScheduleCadence;
  /** Interval for the "Every N minutes" cadence (one of MINUTE_INTERVAL_CHOICES). */
  intervalMinutes: MinuteInterval;
  /** Minute of the hour for the hourly cadence (0–59). */
  minute: number;
  /** Hour of the day for daily/weekly cadences (0–23). */
  hour: number;
  /** Day of week for the weekly cadence (0–6, Sunday = 0). */
  dayOfWeek: number;
}

/** Sensible defaults for a fresh schedule selection (Daily 09:00, Monday if switched to weekly). */
export const DEFAULT_SCHEDULE_SELECTION: ScheduleSelection = {
  cadence: 'none',
  intervalMinutes: DEFAULT_MINUTE_INTERVAL,
  minute: 0,
  hour: 9,
  dayOfWeek: 1,
};

const inRange = (n: number, min: number, max: number): boolean =>
  Number.isInteger(n) && n >= min && n <= max;

/**
 * Build the canonical cron string for a cadence + fields. Returns `null` for `none`
 * (no schedule trigger) or when a field is out of range (the caller should not publish a
 * malformed cron). Field ranges: minute 0–59, hour 0–23, dayOfWeek 0–6.
 */
export function cadenceToCron(sel: ScheduleSelection): string | null {
  switch (sel.cadence) {
    case 'none':
      return null;
    case 'minutes':
      // N=60 is "top of every hour" (`0 * * * *`); the step form only makes sense for
      // sub-hourly intervals that divide the hour, i.e. 5/10/15/30.
      if (!isMinuteInterval(sel.intervalMinutes)) return null;
      return sel.intervalMinutes === 60 ? '0 * * * *' : `*/${sel.intervalMinutes} * * * *`;
    case 'hourly':
      return inRange(sel.minute, 0, 59) ? `${sel.minute} * * * *` : null;
    case 'daily':
      return inRange(sel.hour, 0, 23) ? `0 ${sel.hour} * * *` : null;
    case 'weekly':
      return inRange(sel.hour, 0, 23) && inRange(sel.dayOfWeek, 0, 6)
        ? `0 ${sel.hour} * * ${sel.dayOfWeek}`
        : null;
    default:
      return null;
  }
}

/**
 * Reverse-parse a stored cron string back into a `ScheduleSelection`. Recognises exactly
 * the three canonical shapes this builder emits; returns `null` for anything else (a
 * hand-authored or otherwise unmodelled cron), so the caller can decide how to surface an
 * unknown expression rather than silently coercing it.
 */
export function parseCron(cron: string): ScheduleSelection | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [min, hr, dom, mon, dow] = parts;

  // minutes: `*/N * * * *` (step form only; a plain `0 * * * *` is left to the hourly branch).
  if (hr === '*' && dom === '*' && mon === '*' && dow === '*') {
    const step = min ? /^\*\/(\d+)$/.exec(min) : null;
    if (step) {
      const intervalMinutes = Number(step[1]!);
      if (isMinuteInterval(intervalMinutes)) {
        return { ...DEFAULT_SCHEDULE_SELECTION, cadence: 'minutes', intervalMinutes };
      }
      return null;
    }
  }

  // hourly: `M * * * *`
  if (hr === '*' && dom === '*' && mon === '*' && dow === '*') {
    const minute = Number(min);
    if (min !== '*' && inRange(minute, 0, 59)) {
      return { ...DEFAULT_SCHEDULE_SELECTION, cadence: 'hourly', minute };
    }
    return null;
  }

  // daily: `0 H * * *`
  if (min === '0' && dom === '*' && mon === '*' && dow === '*') {
    const hour = Number(hr);
    if (inRange(hour, 0, 23)) {
      return { ...DEFAULT_SCHEDULE_SELECTION, cadence: 'daily', hour };
    }
    return null;
  }

  // weekly: `0 H * * D`
  if (min === '0' && dom === '*' && mon === '*') {
    const hour = Number(hr);
    const dayOfWeek = Number(dow);
    if (inRange(hour, 0, 23) && inRange(dayOfWeek, 0, 6)) {
      return { ...DEFAULT_SCHEDULE_SELECTION, cadence: 'weekly', hour, dayOfWeek };
    }
    return null;
  }

  return null;
}

/** The browser's IANA timezone, used as the default for a new schedule trigger. */
export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

// ── Active-hours window (v1.10.1) ────────────────────────────────────────────────
//
// A schedule trigger may carry an optional `activeWindow` restricting WHEN it fires to a
// time-of-day window (+ optional weekdays), in the schedule's timezone. The wire shape is
// `{ start:'HH:MM', end:'HH:MM', days?: number[] }`. These pure helpers build it from /
// reverse-parse it into the builder's active-hours state so the derive + prefill agree by
// construction and are unit-tested without a component tree (same rationale as parseCron).

/** The wire shape of a schedule trigger's `activeWindow` (`days` omitted ⇒ every day). */
export interface ActiveWindow {
  start: string;
  end: string;
  days?: number[];
}

/** The builder's active-hours state: enabled flag + start/end + a (possibly empty) days set. */
export interface ActiveHoursState {
  enabled: boolean;
  start: string;
  end: string;
  /** Selected weekdays (0=Sun…6=Sat); empty ⇒ no restriction (every day). */
  days: number[];
}

/**
 * Build the trigger's optional `activeWindow` from the builder's active-hours state.
 * Returns `undefined` when disabled (⇒ the trigger omits `activeWindow`, always active).
 * De-dupes + sorts the weekdays and OMITS `days` entirely when none are chosen (every day).
 */
export function buildActiveWindow(sel: ActiveHoursState): ActiveWindow | undefined {
  if (!sel.enabled) return undefined;
  const days = [...new Set(sel.days)].sort((a, b) => a - b);
  return { start: sel.start, end: sel.end, ...(days.length > 0 ? { days } : {}) };
}

/**
 * Reverse of `buildActiveWindow`: derive the builder's active-hours state from a stored
 * trigger's `activeWindow`. Absent ⇒ disabled (start/end/days left for the caller to fall
 * back to defaults). An absent `days` becomes an empty set (no restriction).
 */
export function parseActiveWindow(
  activeWindow: ActiveWindow | undefined,
): { enabled: boolean; start?: string; end?: string; days: number[] } {
  if (!activeWindow) return { enabled: false, days: [] };
  return {
    enabled: true,
    start: activeWindow.start,
    end: activeWindow.end,
    days: activeWindow.days ?? [],
  };
}

/** Day-of-week labels for the weekly picker, indexed 0 (Sun) – 6 (Sat). */
export const DAY_OF_WEEK_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;
